import { useEffect, useMemo, useState } from 'react';
import { useLocation, useParams, Navigate } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import ProfileCard from './components/ProfileCard';
import VideoIntro from './components/VideoIntro';
import BookingCalendar from './components/BookingCalendar';
import ServicePackagesCard from './components/ServicePackagesCard';
import CareerTimeline from './components/CareerTimeline';
import TestimonialsGrid from './components/TestimonialsGrid';
import RelatedMentors from './components/RelatedMentors';
import type { Mentor } from '../../mocks/mentors';
import { api } from '@/lib/api';
import { adaptLecturer } from '@/lib/adapters';
import { useAuth } from '@/lib/auth';

const MentorPage = () => {
  const { mentorId } = useParams<{ mentorId: string }>();
  const location = useLocation();
  const [mentor, setMentor] = useState<Mentor | null>(null);
  const [hasPurchased, setHasPurchased] = useState(false);
  const [activeBookingId, setActiveBookingId] = useState<string | null>(null);
  const [defaultBookingDate, setDefaultBookingDate] = useState('');
  const [defaultBookingTime, setDefaultBookingTime] = useState('');
  const [showSuccessBanner, setShowSuccessBanner] = useState(false);
  const [bannerState, setBannerState] = useState<'paid' | 'pending'>('pending');
  const { isAuthenticated } = useAuth();
  const parsedMentorId = useMemo(() => {
    return mentorId && /^\d+$/.test(mentorId) ? mentorId : null;
  }, [mentorId]);

  useEffect(() => {
    if (!parsedMentorId) return;
    void api.getLecturerDetail(parsedMentorId).then((detail) => {
      setMentor(adaptLecturer(detail));
    });
  }, [parsedMentorId]);

  useEffect(() => {
    if (!mentor || !location.hash) return;
    const targetId = location.hash.replace('#', '');
    window.setTimeout(() => {
      document.getElementById(targetId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 120);
  }, [location.hash, mentor]);

  const refreshValidBooking = async (lecturerId: string, bookingNo?: string) => {
    const page = await api.getMyBookings({ pageCurrent: 1, pageSize: 100 });
    const lecturerBookings = page.list.filter((item) => String(item.lecturerId) === lecturerId);
    const paidValidBookings = lecturerBookings
      .filter((item) => Number(item.orderStatus) === 2 && ![4, 5].includes(Number(item.bookingStatus ?? 0)))
      .sort((a, b) => {
        const aTime = new Date(a.payTime || a.gmtCreate || 0).getTime();
        const bTime = new Date(b.payTime || b.gmtCreate || 0).getTime();
        return bTime - aTime;
      });

    const matchByBookingNo = bookingNo
      ? paidValidBookings.find((item) => String(item.bookingNo) === bookingNo)
      : undefined;
    const latestPaidBooking = matchByBookingNo || paidValidBookings[0];

    if (latestPaidBooking?.id) {
      setActiveBookingId(String(latestPaidBooking.id));
      setHasPurchased(true);
      setDefaultBookingDate(latestPaidBooking.bookingDate || '');
      setDefaultBookingTime(latestPaidBooking.bookingTime || '');
      return;
    }

    const active = await api.getActiveBookingByLecturer(lecturerId);
    if (active?.id && Number(active.orderStatus) === 2 && ![4, 5].includes(Number(active.bookingStatus ?? 0))) {
      setActiveBookingId(String(active.id));
      setHasPurchased(true);
      setDefaultBookingDate(active.bookingDate || '');
      setDefaultBookingTime(active.bookingTime || '');
      return;
    }

    setActiveBookingId(null);
    setHasPurchased(false);
    setDefaultBookingDate('');
    setDefaultBookingTime('');
  };

  useEffect(() => {
    if (!mentor || !isAuthenticated) {
      setActiveBookingId(null);
      setHasPurchased(false);
      return;
    }
    void refreshValidBooking(mentor.id);
  }, [isAuthenticated, mentor?.id]);

  if (!parsedMentorId) return <Navigate to="/mentors" replace />;
  if (!mentor) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div style={{ paddingTop: '64px' }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-12">
            <div className="grid gap-6 lg:grid-cols-[320px_minmax(0,1fr)]">
              <div className="h-[320px] animate-pulse rounded-3xl bg-white" />
              <div className="space-y-4 rounded-3xl bg-white p-8">
                <div className="h-8 w-48 animate-pulse rounded-xl bg-slate-100" />
                <div className="h-5 w-64 animate-pulse rounded-xl bg-slate-100" />
                <div className="h-4 w-full animate-pulse rounded-xl bg-slate-100" />
                <div className="h-4 w-4/5 animate-pulse rounded-xl bg-slate-100" />
                <div className="grid grid-cols-3 gap-4 pt-4">
                  <div className="h-16 animate-pulse rounded-2xl bg-slate-100" />
                  <div className="h-16 animate-pulse rounded-2xl bg-slate-100" />
                  <div className="h-16 animate-pulse rounded-2xl bg-slate-100" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const handleBookClick = () => {
    document.getElementById('packages-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleGoToPurchase = () => {
    document.getElementById('packages-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handlePurchase = ({ bookingNo, orderStatus }: { bookingNo: string; orderStatus: number }) => {
    const paid = orderStatus === 2;
    setHasPurchased(paid);
    setBannerState(paid ? 'paid' : 'pending');
    setShowSuccessBanner(true);
    if (paid) {
      window.setTimeout(() => {
        document.getElementById('booking-section')?.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    }
    if (mentor && paid) {
      void refreshValidBooking(mentor.id, bookingNo);
    }
    window.setTimeout(() => {
      setShowSuccessBanner(false);
    }, 8000);
  };

  const handleSlotSelect = async (date: string, time: string) => {
    if (!activeBookingId) {
      throw new Error('请先完成有效预约订单');
    }
    await api.updateMentorBookingTime({
      id: activeBookingId,
      bookingDate: date,
      bookingTime: time,
      bookingTimezone: 'HKT',
      preferTime: `${date} ${time}`
    });
    if (mentor) {
      await refreshValidBooking(mentor.id);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 font-sans">
      <Navbar />
      <div style={{ paddingTop: '64px' }}>
        <ProfileCard mentor={mentor} onBook={handleBookClick} />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <div className="flex flex-col sm:flex-row gap-4 mb-3">
            <div className="w-full sm:w-52 flex-shrink-0">
              <VideoIntro mentor={mentor} />
            </div>
            <div className="flex-1 flex items-center">
              <div className="bg-violet-50 border border-violet-100 rounded-xl p-4 w-full">
                <div className="flex items-center gap-3 mb-2">
                  <i className="ri-information-line text-violet-500 text-lg" />
                  <span className="text-violet-700 font-semibold text-sm">為什麼選擇 {mentor.name}？</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3">
                  {[
                    { icon: 'ri-building-4-line', text: '頂尖外商內部專家' },
                    { icon: 'ri-group-line', text: `${mentor.sessions}+ 學員諮詢經驗` },
                    { icon: 'ri-translate-2', text: `${mentor.languages.slice(0, 2).join('/')} 授課` }
                  ].map((item) => (
                    <div key={item.text} className="flex items-center gap-2 text-sm text-violet-600">
                      <i className={`${item.icon} flex-shrink-0`} />
                      <span className="text-xs">{item.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-100 px-5 py-4 mb-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="flex items-center gap-2 flex-shrink-0">
              <div className="w-6 h-6 flex items-center justify-center bg-violet-100 rounded-full">
                <i className="ri-route-line text-violet-600 text-xs" />
              </div>
              <span className="text-sm font-semibold text-[#0A1628]">預約流程</span>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              {[
                { step: '1', label: '確認價格' },
                { step: '2', label: '線上付款' },
                { step: '3', label: '預約成功' },
                { step: '4', label: '選擇時間' },
                { step: '5', label: '開始諮詢' }
              ].map((item, index, list) => (
                <div key={item.step} className="flex items-center gap-2">
                  <div className="flex items-center gap-1.5">
                    <div className="w-5 h-5 flex items-center justify-center bg-violet-600 text-white text-[10px] font-bold rounded-full flex-shrink-0">
                      {item.step}
                    </div>
                    <span className="text-xs text-slate-600 whitespace-nowrap">{item.label}</span>
                  </div>
                  {index < list.length - 1 && <i className="ri-arrow-right-s-line text-slate-300 text-base" />}
                </div>
              ))}
            </div>
          </div>

          <div
            className="overflow-hidden transition-all duration-500 ease-out"
            style={{
              maxHeight: showSuccessBanner ? '120px' : '0px',
              opacity: showSuccessBanner ? 1 : 0,
              marginBottom: showSuccessBanner ? '16px' : '0px'
            }}
          >
            <div className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-xl px-5 py-4">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 flex items-center justify-center bg-emerald-100 rounded-full flex-shrink-0">
                  <i className="ri-checkbox-circle-fill text-emerald-500 text-lg" />
                </div>
                <div>
                  <p className="text-emerald-800 font-bold text-sm">{bannerState === 'paid' ? '付款成功！請選擇你的諮詢時間' : '預約訂單已建立'}</p>
                  <p className="text-emerald-600 text-xs mt-0.5">
                    {bannerState === 'paid'
                      ? '行事曆已解鎖 · 選好時間後點擊確認，導師將在 24 小時內傳送會議連結'
                      : '你可以前往帳戶中心查看付款與預約處理狀態'}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowSuccessBanner(false)}
                className="w-7 h-7 flex items-center justify-center text-emerald-400 hover:text-emerald-600 hover:bg-emerald-100 rounded-full cursor-pointer transition-colors flex-shrink-0 ml-3"
              >
                <i className="ri-close-line text-base" />
              </button>
            </div>
          </div>

          <div id="booking-section" className="flex flex-col lg:flex-row gap-4 sm:gap-6">
            <div id="packages-section" className="w-full lg:w-80 flex-shrink-0">
              <ServicePackagesCard mentor={mentor} hasPurchased={hasPurchased} onPurchase={handlePurchase} onGoToBooking={() => document.getElementById('booking-section')?.scrollIntoView({ behavior: 'smooth' })} />
            </div>
            <div className="flex-1">
              <BookingCalendar
                hasPurchased={hasPurchased}
                onGoToPurchase={handleGoToPurchase}
                onSlotSelect={handleSlotSelect}
                defaultDate={defaultBookingDate}
                defaultTime={defaultBookingTime}
              />
            </div>
          </div>

          <div className="mt-6"><CareerTimeline mentor={mentor} /></div>
          <div className="mt-6"><TestimonialsGrid mentor={mentor} /></div>
          <RelatedMentors currentMentor={mentor} />
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default MentorPage;
